import { v4 as uuidv4 } from "uuid";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logDataToConsole, logError } from "../../utils/customLogger";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import {
  createRequestOptions,
  getAuthHeaders,
  handleApiResponse,
} from "../utils/apiHelpers";

interface StatusTrackingFilters {
  filename?: string[];
  [key: string]: any;
}

interface RunNycbsPharmaStatusTrackingPayload {
  org_id: string;
  filters: StatusTrackingFilters;
  request_id: string;
  [key: string]: any;
}

export interface RunNycbsPharmaStatusTrackingResponse {
  success: boolean;
  message: string;
  [key: string]: any;
}

/**
 * Runs status tracking for NYCBS pharma files
 * @param filenames - Array of filenames to track status for
 * @param signal - Optional AbortSignal for canceling the request
 * @returns Promise with the API response
 */
export const runNycbsPharmaStatusTracking = async (
  filenames: string[],
  signal?: AbortSignal,
): Promise<RunNycbsPharmaStatusTrackingResponse> => {
  const orgId = getOrgIdForFetchExternalWorklist();

  const payload: RunNycbsPharmaStatusTrackingPayload = {
    org_id: orgId,
    filters: {
      filename: filenames,
    },
    request_id: uuidv4(),
  };

  const headers = await getAuthHeaders();
  const requestOptions = createRequestOptions(payload, headers, signal);

  try {
    const response = await fetch(
      API_ENDPOINTS.RUN_NYCBS_PHARMA_STATUS_TRACKING,
      requestOptions,
    );
    const result = await handleApiResponse(response);
    logDataToConsole("runNycbsPharmaStatusTracking", result);
    return result;
  } catch (error) {
    if ((error as Error).name === "AbortError") {
      logDataToConsole("runNycbsPharmaStatusTracking", "Request aborted");
      throw error;
    }
    logError(error as Error, "Error in runNycbsPharmaStatusTracking");
    throw error;
  }
};

export interface RunNycbsPharmaStatusTrackingByDateRangePayload {
  org_id: string;
  filters: {
    date_of_filing: {
      start_date: string;
      end_date: string;
    };
  };
}

/**
 * Runs status tracking for NYCBS pharma by date of filing range
 * @param payload - { org_id, filters: { date_of_filing: { start_date, end_date } } } with dates as MM/DD/YYYY
 * @param signal - Optional AbortSignal for canceling the request
 */
export const runNycbsPharmaStatusTrackingByDateRange = async (
  payload: RunNycbsPharmaStatusTrackingByDateRangePayload,
  signal?: AbortSignal,
): Promise<RunNycbsPharmaStatusTrackingResponse> => {
  const headers = await getAuthHeaders();
  const requestOptions = createRequestOptions(
    payload as unknown as Record<string, unknown>,
    headers,
    signal,
  );

  try {
    const response = await fetch(
      API_ENDPOINTS.RUN_NYCBS_PHARMA_STATUS_TRACKING,
      requestOptions,
    );
    const result = await handleApiResponse(response);
    logDataToConsole("runNycbsPharmaStatusTrackingByDateRange", result);
    return result;
  } catch (error) {
    if ((error as Error).name === "AbortError") {
      logDataToConsole(
        "runNycbsPharmaStatusTrackingByDateRange",
        "Request aborted",
      );
      throw error;
    }
    logError(
      error as Error,
      "Error in runNycbsPharmaStatusTrackingByDateRange",
    );
    throw error;
  }
};
